import React, { useEffect, useRef, useState } from 'react';
import { IoMicOff, IoPin, IoEllipsisVertical, IoHandLeft, IoVolumeHigh, IoPersonRemove, IoExpand, IoBrush, IoTrashBin } from 'react-icons/io5';
import { imageSrc } from '../../utils/avatar';
import { useAttachedTrack } from '../../hooks/useAttachedTrack';
import { tileGradient, initialsOf, avatarColor } from '../../utils/meetingAvatar';
import MeetingAnnotations from './MeetingAnnotations';
import { useLanguage } from '../../i18n/LanguageContext';

const avatarUrl = (imageUrl) => imageSrc(imageUrl, { size: 128 });

// A signed-in Cyclone user's avatar travels in their LiveKit token metadata
// (createMeetingToken), which is the only way a guest - who may not call the
// user API at all - gets to see a real picture instead of initials.
function avatarFromMetadata(metadata) {
    if (!metadata) return null;
    try {
        const parsed = JSON.parse(metadata);
        return parsed && parsed.imageUrl ? avatarUrl(parsed.imageUrl) : null;
    } catch (error) {
        return null;
    }
}

// One participant's tile. Unlike VideoGridOverlay's VideoTile (group rooms,
// where everyone is a known member with a profile picture), this one has to
// stand on its own for a stranger: a per-identity colour and their initials
// instead of a shared fallback logo, plus the hover controls a meeting needs -
// pin, per-participant volume, and the host's remove.
export default function MeetingTile({
    participant, track, audioTrack, screenShare = false, isPinned, onTogglePin,
    handRaised, audioLevel = 0, volume, onVolumeChange, canRemove, onRemove,
    annotations, localIdentity, onStroke, onPointer, onClearAnnotations
}) {
    const { t } = useLanguage();
    const [menuOpen, setMenuOpen] = useState(false);
    const [drawing, setDrawing] = useState(false);
    const menuRef = useRef(null);
    const tileRef = useRef(null);

    // A screen share is never "camera muted" - its track is either published
    // or it isn't - so the cameraEnabled gate (which exists because turning a
    // camera off mutes rather than unpublishes) must not apply to it.
    const showVideo = track && (screenShare || participant.cameraEnabled);
    const videoRef = useAttachedTrack(track, showVideo);

    // Screen-share audio is the one audio track useVoiceConnection hands over
    // instead of playing itself (unlike microphones, which it routes through a
    // hidden element and a gain node). In a group room ScreenShareOverlay owns
    // it; here the screen-share tile does, so a shared tab's sound actually
    // comes out. Attaching it anywhere else would double-play it.
    const audioRef = useAttachedTrack(audioTrack, !!audioTrack);

    const displayName = participant.name || participant.identity;
    const photo = avatarFromMetadata(participant.metadata);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const close = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
        };
        // Capture phase: the tile itself stops propagation on some clicks, and
        // a menu that only closes on its own button is a menu that gets stuck.
        document.addEventListener('mousedown', close, true);
        return () => document.removeEventListener('mousedown', close, true);
    }, [menuOpen]);

    const classes = [
        'MeetTile',
        participant.speaking && !screenShare ? 'MeetTileSpeaking' : '',
        participant.isLocal ? 'MeetTileLocal' : '',
        screenShare ? 'MeetTileScreen' : ''
    ].filter(Boolean).join(' ');

    // Browser-native fullscreen on the tile element itself, rather than a
    // CSS-only "cover the app" mode: a shared screen is usually the thing
    // worth filling an actual monitor with, and this is also the only way to
    // escape the window chrome in the Electron build.
    const toggleFullscreen = () => {
        const element = tileRef.current;
        if (!element) return;
        if (document.fullscreenElement === element) document.exitFullscreen();
        else if (element.requestFullscreen) element.requestFullscreen().catch(() => {});
    };

    // The speaking ring's intensity rides this rather than being a fixed on/off
    // border: --tile-level is read by .MeetTileSpeaking in Meeting.css. Floored
    // at 0.35 so a quiet speaker is still clearly marked, and rounded so an
    // inline style doesn't churn on every 100ms sample.
    const levelVar = participant.speaking
        ? Math.round(Math.max(0.35, Math.min(1, audioLevel * 2)) * 20) / 20
        : 0;

    // Only a screen share that is actually painting can be annotated - the
    // normalized coordinate space is derived from the decoded frame's own
    // dimensions (videoContentRect.js), which do not exist before then.
    const annotateable = screenShare && showVideo && !!annotations;

    const tileStyle = { '--tile-level': levelVar };
    if (!showVideo) tileStyle.background = tileGradient(participant.identity);

    return (
        <div ref={tileRef} className={classes} style={tileStyle}>
            {showVideo ? (
                // muted is mandatory even on remote tiles: audio already plays
                // through useVoiceConnection's hidden <audio>/gain-node path
                // (that is what makes the volume slider work at all), and an
                // unmuted <video> would double it and bypass the gain node.
                // objectFit differs by kind - a camera is cropped to fill the
                // tile, a shared screen must never be.
                <video
                    ref={videoRef}
                    className={screenShare ? 'MeetTileVideo MeetTileVideoContain' : 'MeetTileVideo'}
                    autoPlay
                    playsInline
                    muted
                />
            ) : (
                <div className="MeetTileAvatarWrap">
                    {photo
                        ? <img className="MeetTileAvatarImg" src={photo} alt={displayName} />
                        : <div className="MeetTileInitials">{initialsOf(displayName)}</div>}
                </div>
            )}

            {/* Pointing and drawing, only over a live screen share - there is
                nothing to annotate on a camera feed, and the coordinate space
                this depends on only exists once a picture is decoding. */}
            {annotateable && (
                <>
                    <MeetingAnnotations
                        videoRef={videoRef}
                        drawing={drawing}
                        color={avatarColor(localIdentity)}
                        annotations={annotations}
                        onStroke={onStroke}
                        onPointer={onPointer}
                        localIdentity={localIdentity}
                    />
                    <div className="MeetAnnotateTools">
                        <button
                            className={`MeetAnnotateBtn ${drawing ? 'MeetAnnotateBtnActive' : ''}`}
                            onClick={() => setDrawing((on) => !on)}
                            title={drawing ? t('meeting.annotate.stop') : t('meeting.annotate.draw')}
                        >
                            <IoBrush />
                        </button>
                        <button
                            className="MeetAnnotateBtn"
                            onClick={() => onClearAnnotations(participant.isLocal ? 'all' : 'mine')}
                            title={participant.isLocal ? t('meeting.annotate.clearAll') : t('meeting.annotate.clearMine')}
                        >
                            <IoTrashBin />
                        </button>
                    </div>
                </>
            )}

            {/* Rendered alongside the video rather than inside the branch
                above, so it survives the sharer toggling their own camera. */}
            {audioTrack && <audio ref={audioRef} autoPlay />}

            <div className="MeetTileFooter">
                <span className="MeetTileName">
                    {screenShare ? t('meeting.screenOf', { name: displayName }) : displayName}
                    {participant.isLocal && !screenShare && <span className="MeetTileYou"> {t('meeting.youSuffix')}</span>}
                </span>
            </div>

            <div className="MeetTileBadges">
                {handRaised && <span className="MeetTileBadge MeetTileHand" title={t('meeting.handRaised')}><IoHandLeft /></span>}
                {participant.muted && !screenShare && <span className="MeetTileBadge MeetTileMuted" title={t('meeting.muted')}><IoMicOff /></span>}
                {isPinned && <span className="MeetTileBadge" title={t('meeting.pinned')}><IoPin /></span>}
            </div>

            {/* Hover-only (see .MeetTileHover in Meeting.css) so the grid stays
                calm; kept mounted rather than conditionally rendered so the
                open menu doesn't vanish the moment the pointer crosses a gap. */}
            <div className="MeetTileHover" ref={menuRef}>
                <button
                    className={`MeetTileHoverBtn ${isPinned ? 'MeetTileHoverBtnActive' : ''}`}
                    onClick={onTogglePin}
                    title={isPinned ? t('meeting.unpin') : t('meeting.pin')}
                >
                    <IoPin />
                </button>
                <button className="MeetTileHoverBtn" onClick={toggleFullscreen} title={t('meeting.fullscreen')}>
                    <IoExpand />
                </button>
                {!participant.isLocal && (
                    <button className="MeetTileHoverBtn" onClick={() => setMenuOpen((open) => !open)} title={t('meeting.more')}>
                        <IoEllipsisVertical />
                    </button>
                )}
                {menuOpen && !participant.isLocal && (
                    <div className="MeetTileMenu glass">
                        {/* Mirrors the group-room volume slider: 0-300%, where
                            anything above 100% goes through the Web Audio gain
                            node rather than the clamped element volume. */}
                        <label className="MeetTileMenuRow">
                            <IoVolumeHigh />
                            <input
                                type="range"
                                min="0"
                                max="3"
                                step="0.05"
                                value={volume}
                                onChange={(event) => onVolumeChange(participant.identity, Number(event.target.value))}
                            />
                            <span className="MeetTileMenuValue">{Math.round(volume * 100)}%</span>
                        </label>
                        {canRemove && (
                            <button
                                className="MeetTileMenuBtn MeetTileMenuDanger"
                                onClick={() => { setMenuOpen(false); onRemove(participant.identity, displayName); }}
                            >
                                <IoPersonRemove /> {t('meeting.removeParticipant')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
